/**
 * Glue between the AOME container primitives (media-crypto.js), the key stores
 * (media-key.js for the v1 master key, media-convkey.js for v2 conversation
 * keys) and the parts of the UI which move media bytes around.
 *
 * RECEIVE is permissive and must stay that way forever: a legacy (unencrypted)
 * entity takes the exact same path it took before this feature existed, v1 and
 * v2 entities are both decrypted, and a blob whose bytes are not an AOME
 * container is passed through even if `enc` was present.
 *
 * SEND is fail-closed as of v2 (docs/MEDIA_ENCRYPTION_V2.md §4.1): the only
 * situation which still produces plaintext is a server which does not implement
 * the v2 endpoint at all *and* provides no v1 master key. Every other failure —
 * KMS outage, 401/403, WebCrypto error, escrow wrap failure while the escrow
 * switch is on — rejects the send. Silently uploading plaintext because a key
 * service was down is the exact defect v2 exists to remove.
 */

import { MediaCryptoError, decryptBlob, decryptFromDownload, encryptBlob, encryptForUpload,
  escrowWrap, isCryptoAvailable, isEncrypted, looksEncrypted, makeContentKey, makeEncMetadataV2,
  parseEncMetadata, unwrapKeyV2, wrapKeyV2, DEFAULT_FRAME_SIZE }
  from './media-crypto.js';
import { mediaKeyStore } from './media-key.js';
import { ConvKeyUnsupportedError, convKeyStore } from './media-convkey.js';

// ---------------------------------------------------------------------------
// Receive context
//
// The download/decrypt helpers are called from deep inside the rendering tree
// (formatters.js, attachment.jsx, decrypted-media.jsx), none of which carry the
// topic. v2 needs it: the conversation key is per topic, and spec §3.3 requires
// the decoder to use the topic the message was actually *received* in rather
// than the `tpc` hint inside `enc`. MessagesView publishes the topic it is
// currently rendering here; callers can also override per call via
// `opts.topic` / `opts.tinode`.
// ---------------------------------------------------------------------------

let _ctxTinode = null;
let _ctxTopic = null;

/** Publish the Tinode instance and topic whose messages are being rendered. */
export function setMediaContext(tinode, topicName) {
  _ctxTinode = tinode || null;
  _ctxTopic = (typeof topicName == 'string' && topicName) ? topicName : null;
}

/** Forget the receive context and every cached conversation key. Call on logout. */
export function clearMediaContext() {
  _ctxTinode = null;
  _ctxTopic = null;
  convKeyStore.clear();
}

/**
 * A video entity keeps the metadata of its independently-encrypted preview in
 * `encpre` (spec §3). Reshape it into something `parseEncMetadata` understands.
 *
 * @param {Object} data - Drafty entity data.
 * @returns {Object|null} a pseudo entity-data carrying the preview's `enc`.
 */
export function previewEncData(data) {
  return (data && data.encpre) ? {enc: data.encpre} : null;
}

/** True when the entity's main payload (`ref`) is encrypted. */
export function isRefEncrypted(data) {
  return isEncrypted(data);
}

/** True when the entity's video preview (`preref`) is encrypted. */
export function isPreviewEncrypted(data) {
  return isEncrypted(previewEncData(data));
}

/**
 * True when encryption can and should be applied to an outgoing upload.
 * Encryption is best-effort on send: when this is false we upload plaintext.
 */
export function canEncrypt() {
  return mediaKeyStore.hasKey() && isCryptoAvailable();
}

/**
 * v1 send path, unchanged from before v2 shipped: encrypt under the app-wide
 * master key when there is one, upload plaintext when there is not.
 *
 * Reached only when the server answered the conversation-key endpoint with 404,
 * i.e. it predates v2 entirely. Every other failure mode fails closed instead.
 */
function encryptForSendV1(blob, opts) {
  if (!canEncrypt()) {
    return Promise.resolve({blob: blob, enc: null});
  }
  return encryptForUpload(blob, mediaKeyStore.getKey(), opts);
}

/**
 * Encrypt an outgoing blob. FAIL-CLOSED (docs/MEDIA_ENCRYPTION_V2.md §4.1).
 *
 * Order of operations:
 *   1. fetch CK_conv for `topic`;
 *   2. encrypt the container under a fresh random CK_file;
 *   3. wrap CK_file under CK_conv, AAD-bound to the topic;
 *   4. if and only if the server says escrow is ON, additionally wrap CK_file to
 *      the escrow public key. A failure here REJECTS the send — never send
 *      without `esc` while the switch is on, or "on" would be advisory.
 *
 * The ONLY tolerated downgrade is HTTP 404 from the convkey endpoint, which
 * means the route is not registered and the server predates v2; that falls back
 * to the v1 master-key path. A 401/403/503, a network failure, a timeout or any
 * crypto error REJECTS. Nothing in this function ever resolves with plaintext
 * after a v2 server has been observed.
 *
 * @param {Tinode} tinode - connected, authenticated Tinode instance.
 * @param {string} topic - topic the message will be sent to.
 * @param {Blob} blob - plaintext payload.
 * @param {Object} [opts] - {frameSize, onProgress}.
 * @returns {Promise<{blob: Blob, enc: Object|null}>} blob to upload and the
 *   metadata to stamp onto the Drafty entity.
 */
export async function encryptForSend(tinode, topic, blob, opts) {
  if (!blob) {
    return {blob: blob, enc: null};
  }

  let convKey;
  try {
    convKey = await convKeyStore.get(tinode, topic);
  } catch (err) {
    if (err instanceof ConvKeyUnsupportedError) {
      // Server predates v2. This is the one permitted downgrade.
      return encryptForSendV1(blob, opts);
    }
    // ConvKeyUnavailableError and anything unexpected: fail closed.
    throw err;
  }

  if (!isCryptoAvailable()) {
    throw new MediaCryptoError(
      'WebCrypto is unavailable; a secure context (HTTPS or localhost) is required to send media',
      'no_webcrypto');
  }

  // The server canonicalizes the topic name before deriving CK_conv and echoes
  // the canonical name back; a p2p topic's wire name is the peer's uid and so
  // differs on each side. The AAD and `enc.tpc` must both use the canonical
  // name, or the two participants would bind their wraps to different strings.
  const convTopic = convKey.topic;

  const options = opts || {};
  const frameSize = options.frameSize || DEFAULT_FRAME_SIZE;
  const contentKey = makeContentKey();
  const encrypted = await encryptBlob(blob, contentKey, {
    frameSize: frameSize,
    onProgress: options.onProgress
  });
  const wrapped = await wrapKeyV2(contentKey, convKey.ck, convTopic);

  let escrowCt = null;
  if (convKey.escrow && convKey.escrow.on) {
    // Deliberately not caught: an escrow wrap that cannot be produced must kill
    // the send, not silently downgrade it to an unescrowed message.
    escrowCt = await escrowWrap(contentKey, convKey.escrow.pub);
  }

  return {
    blob: encrypted,
    enc: makeEncMetadataV2({
      wrapped: wrapped.wrapped,
      nonce: wrapped.nonce,
      ckid: convKey.ckid,
      topic: convTopic,
      plainSize: blob.size,
      frameSize: frameSize,
      escrowCt: escrowCt,
      escrowAlg: convKey.escrow ? convKey.escrow.alg : undefined,
      escrowKid: convKey.escrow ? convKey.escrow.kid : undefined
    })
  };
}

/**
 * Backwards-compatible wrapper around `encryptForSend`.
 *
 * Kept because call sites written against the v1 API still use this name. The
 * historical "never rejects, fall back to plaintext" contract is GONE: this now
 * rethrows exactly what `encryptForSend` throws. Callers must surface the error
 * to the user instead of uploading the plaintext blob.
 *
 * @param {Tinode} tinode - connected, authenticated Tinode instance.
 * @param {string} topic - topic the message will be sent to.
 * @param {Blob} blob - plaintext payload.
 * @param {Object} [opts] - {frameSize, onProgress}.
 */
export function encryptIfPossible(tinode, topic, blob, opts) {
  return encryptForSend(tinode, topic, blob, opts);
}

/**
 * Add the `enc` (or `encpre`) marker to the entity most recently appended to a
 * Drafty document.
 *
 * `Drafty.attachFile`/`insertImage`/`insertVideo` push exactly one entity onto
 * `content.ent` and return the document, so the entity we just created is the
 * last one. The upload URL arrives later through `urlPromise` and mutates the
 * same `ent.data` object, so stamping here is safe and order-independent.
 *
 * @param {Object} msg - Drafty document.
 * @param {Object|null} enc - metadata from `encryptForUpload`; no-op when null.
 * @param {string} [field] - 'enc' (default) or 'encpre'.
 * @returns {Object} msg, for chaining.
 */
export function stampEnc(msg, enc, field) {
  if (!enc || !msg || !Array.isArray(msg.ent) || msg.ent.length == 0) {
    return msg;
  }
  const ent = msg.ent[msg.ent.length - 1];
  if (ent && ent.data) {
    ent.data[field || 'enc'] = enc;
  }
  return msg;
}

/**
 * Re-attach `enc` / `encpre` markers that a tinode-sdk Drafty transform dropped.
 *
 * `Drafty.sanitizeEntities()` and `Drafty.replyContent()` run entity data through
 * the SDK's `copyEntData()`, which keeps only a whitelist of known fields and
 * additionally discards every object-valued field. `enc` is both unknown and an
 * object, so it is removed twice over — the quoted attachment keeps its `ref` but
 * loses the key needed to read it and renders as a broken image.
 *
 * (Measured against tinode-sdk 0.25.x: `Drafty.forwardedContent()` does NOT use
 * `copyEntData` and preserves `enc` on its own. It is passed through here anyway
 * so a future SDK change cannot silently reintroduce the bug.)
 *
 * Entities are matched by `ref`/`preref` rather than by array index, because the
 * reply transform reorders and truncates entities.
 *
 * @param {Object} transformed - document returned by the SDK transform.
 * @param {Object} original - document that was passed into it.
 * @returns {Object} `transformed`, mutated in place, for chaining.
 */
export function preserveEncMarkers(transformed, original) {
  if (!transformed || !Array.isArray(transformed.ent) ||
      !original || !Array.isArray(original.ent)) {
    return transformed;
  }
  const byRef = new Map();
  const byPreref = new Map();
  original.ent.forEach(ent => {
    const d = ent && ent.data;
    if (!d) {
      return;
    }
    if (d.enc && typeof d.ref == 'string') {
      byRef.set(d.ref, d.enc);
    }
    if (d.encpre && typeof d.preref == 'string') {
      byPreref.set(d.preref, d.encpre);
    }
  });
  if (byRef.size == 0 && byPreref.size == 0) {
    return transformed;
  }
  transformed.ent.forEach(ent => {
    const d = ent && ent.data;
    if (!d) {
      return;
    }
    if (!d.enc && byRef.has(d.ref)) {
      d.enc = byRef.get(d.ref);
    }
    if (!d.encpre && byPreref.has(d.preref)) {
      d.encpre = byPreref.get(d.preref);
    }
  });
  return transformed;
}

/**
 * Fetch a media URL and decrypt it if the entity says it is encrypted.
 *
 * @param {string} url - already authorized URL (see Tinode.authorizeURL).
 * @param {Object|null} encData - entity data carrying the `enc` marker, or null
 *   for a payload known to be plaintext.
 * @param {Object} [opts] - {mimeType, onProgress, signal}.
 * @returns {Promise<Blob>} the plaintext blob.
 */
export function fetchAndDecrypt(url, encData, opts) {
  const options = opts || {};
  return fetch(url, options.signal ? {signal: options.signal} : undefined)
    .then(resp => {
      if (!resp.ok) {
        throw new Error(`Download failed: ${resp.status} ${resp.statusText}`);
      }
      return resp.blob();
    })
    .then(blob => {
      const meta = parseEncMetadata(encData);
      if (meta && meta.version == 2) {
        return decryptV2(blob, meta, options);
      }
      // Legacy plaintext (meta == null) and v1 take the original path untouched.
      return decryptFromDownload(blob, encData, mediaKeyStore.getKey(), {
        mimeType: options.mimeType,
        onProgress: options.onProgress
      });
    });
}

/**
 * Decrypt a v2 payload: resolve CK_conv for the topic the message was received
 * in, unwrap CK_file, open the container.
 *
 * Spec §3.3: the receiving topic wins over `enc.tpc`. The key is always fetched
 * for the topic the message actually arrived in; `enc.tpc` is then compared
 * against the CANONICAL name the server derived that key from (a p2p topic's
 * wire name is the peer's uid and differs on each side, so the sender's `tpc`
 * can never equal the receiver's wire name). A disagreement means the message
 * was relayed out of its topic and is a decrypt FAILURE — `tpc` is never used
 * to go fetch a different conversation key.
 */
async function decryptV2(blob, meta, options) {
  const head = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
  if (!looksEncrypted(head)) {
    // Marked encrypted but the bytes are not a container — a partially migrated
    // file. Pass it through rather than failing the render, same as v1.
    return blob;
  }

  const tinode = options.tinode || _ctxTinode;
  // The topic the message was received in. Falls back to the `tpc` hint only
  // when there is no receive context at all (e.g. an out-of-tree preview); the
  // AAD still enforces the binding in that case, it just cannot be checked
  // before the unwrap is attempted.
  const received = options.topic || _ctxTopic;
  const requested = received || meta.topic;
  if (!requested) {
    throw new MediaCryptoError('Cannot determine the topic of an encrypted attachment', 'no_topic');
  }

  let entry = await convKeyStore.get(tinode, requested);
  if (meta.ckid && entry.ckid && meta.ckid != entry.ckid) {
    // The conversation key was rotated since it was cached (spec §4.2): refetch
    // once before declaring the message undecryptable.
    convKeyStore.invalidate(requested);
    entry = await convKeyStore.get(tinode, requested);
  }

  if (received && meta.topic && meta.topic != entry.topic) {
    throw new MediaCryptoError(
      `Attachment is bound to topic '${meta.topic}' but was received in '${entry.topic}'`,
      'topic_mismatch');
  }

  const contentKey = await unwrapKeyV2(meta.wrapped, meta.nonce, entry.ck, entry.topic);
  return decryptBlob(blob, contentKey, {
    mimeType: options.mimeType,
    onProgress: options.onProgress
  });
}

export { MediaCryptoError, isCryptoAvailable, isEncrypted, convKeyStore };
